import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { Spinner } from '~shared/components/Spinner';
import { QuizContextProvider } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import QuizEditor from '~teacher/components/QuizEditor/QuizEditor';
import { WorkspaceQuizEditorHeader } from '~teacher/components/QuizEditor/QuizEditorHeader';
import { navigateByFolderId } from '~teacher/components/workspaces/WorkspaceQuizzes/utils/navigateByItemType';
import {
  TeacherRouter,
  useSocrativeRouter,
} from '~teacher/context/RouterContext';
import { useWorkspaceQuizLock } from '~teacher/hook/workspaces/useWorkspaceQuizLock';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';
import { populateQuizData } from '~teacher/slices/quiz/quizAppThunks';
import { selectEditingQuiz } from '~teacher/slices/quiz/quizSelectors';
import {
  beginEditingQuiz,
  changeStatusToOk,
  refreshEditingQuizQuestions,
} from '~teacher/slices/quiz/quizSlice';

import { LockStatus } from '../../../api/workspaces/lock';
import { WorkspaceQuizResponse } from '../../../api/workspaces/quizzes/getWorkspaceQuiz';
import { RefreshWorkspaceLock } from './RefreshWorkspaceLock';

export type WorkspaceQuizEditorProps = Pick<
  WorkspaceQuizResponse,
  'workspaceId'
> & {
  quiz: WorkspaceQuizResponse['quiz'] & { deleted: boolean };
  workspaceQuizId: string;
  parentFolderId: string;
};

function useQuizDeletedOrSaved(
  workspaceId: string,
  parentFolderId: string,
  router: TeacherRouter
) {
  const queryClient = useQueryClient();
  return React.useCallback(() => {
    queryClient.invalidateQueries(workspaceKeys.allQuizzes(workspaceId));
    navigateByFolderId({
      router,
      isDeleted: false,
      workspaceFolderId: parentFolderId,
      workspaceId: workspaceId,
    });
  }, [parentFolderId, router, workspaceId, queryClient]);
}

export function WorkspaceQuizEditor({
  workspaceId,
  workspaceQuizId,
  quiz,
  parentFolderId,
}: WorkspaceQuizEditorProps) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const router = useSocrativeRouter();
  const workspaceQuizLock = useWorkspaceQuizLock({
    workspaceQuizId,
    workspaceId,
  });

  const editQuiz: ReturnType<typeof selectEditingQuiz> =
    useSelector(selectEditingQuiz);

  React.useEffect(() => {
    dispatch(populateQuizData(quiz)).then(() => {
      if (editQuiz?.id !== quiz.id) {
        dispatch(
          beginEditingQuiz({
            quizId: quiz.id,
          })
        );
      } else if (editQuiz?.lastUpdated !== quiz.lastUpdated) {
        dispatch(refreshEditingQuizQuestions({ quizId: quiz.id }));
      }
      dispatch(changeStatusToOk());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    quiz.id,
    quiz.lastUpdated,
    editQuiz?.id,
    editQuiz?.lastUpdated,
  ]);

  const isQuizLoaded = useSelector(selectEditingQuiz);

  const onQuizDeletedOrSaved = useQuizDeletedOrSaved(
    workspaceId,
    parentFolderId,
    router
  );

  if (isQuizLoaded) {
    const isQuizEditorLocked =
      workspaceQuizLock.data?.lockStatus !== LockStatus.LockedByYou;

    return (
      <>
        {workspaceQuizLock.isSuccess &&
        workspaceQuizLock.data.lockStatus === LockStatus.LockedByYou ? (
          <RefreshWorkspaceLock
            workspaceId={workspaceId}
            workspaceQuizId={workspaceQuizId}
            quizId={quiz.id}
          />
        ) : null}
        <QuizContextProvider
          quizId={quiz.id}
          quizType="workspace"
          workspaceId={workspaceId}
          workspaceQuizId={workspaceQuizId}
        >
          <QuizEditor
            refreshQuestions={() =>
              queryClient.invalidateQueries(
                workspaceKeys.byWorkspaceQuizId({
                  workspaceQuizId: workspaceQuizId,
                  workspaceId: workspaceId,
                })
              )
            }
            isLocked={isQuizEditorLocked}
            onSaveAndExit={onQuizDeletedOrSaved}
            onQuizDeleted={onQuizDeletedOrSaved}
            quizHeaderProp={({ isControlsDisabled, onHandleSaveAndExit }) => (
              <WorkspaceQuizEditorHeader
                workspaceQuizId={workspaceQuizId}
                workspaceId={workspaceId}
                isControlsDisabled={isControlsDisabled}
                onHandleSaveAndExit={onHandleSaveAndExit}
              />
            )}
            router={router}
          />
        </QuizContextProvider>
      </>
    );
  } else {
    return (
      <div className="my-12 mx-auto">
        <Spinner />
      </div>
    );
  }
}
